import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { loadTrustTierHint, useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../utils/apiError';
import { TIER_TTL_OPTIONS, TRUST_TIER_LABELS, TrustTier } from '../auth/tokenManager';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [trustTier, setTrustTier] = useState<TrustTier>(() => loadTrustTierHint().tier);
  const [ttlMinutes, setTtlMinutes] = useState<number>(() => {
    const hint = loadTrustTierHint();
    return hint.ttlMinutes ?? TIER_TTL_OPTIONS[hint.tier][TIER_TTL_OPTIONS[hint.tier].length - 1].minutes;
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const ttlChoices = TIER_TTL_OPTIONS[trustTier];

  const handleTierChange = (_event: React.MouseEvent<HTMLElement>, next: TrustTier | null) => {
    if (!next) {
      return;
    }
    setTrustTier(next);
    const choices = TIER_TTL_OPTIONS[next];
    setTtlMinutes(choices[choices.length - 1].minutes);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password, { trustTier, ttlMinutes });
      navigate('/dashboard');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            Kalpa Niyama
          </Typography>
          <Typography component="h2" variant="h5" gutterBottom>
            Sign In
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                How much do you trust this device?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={trustTier}
                  onChange={handleTierChange}
                  aria-label="Device trust level"
                >
                  {(Object.keys(TRUST_TIER_LABELS) as TrustTier[]).map((tier) => (
                    <ToggleButton key={tier} value={tier} aria-label={TRUST_TIER_LABELS[tier]}>
                      {TRUST_TIER_LABELS[tier]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  select
                  size="small"
                  label="Stay signed in"
                  value={ttlMinutes}
                  onChange={(e) => setTtlMinutes(Number(e.target.value))}
                  disabled={ttlChoices.length === 1}
                  sx={{ minWidth: 140 }}
                >
                  {ttlChoices.map((choice) => (
                    <MenuItem key={choice.minutes} value={choice.minutes}>
                      {choice.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
            <Box textAlign="center">
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/register')}
                type="button"
              >
                Don't have an account? Sign Up
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
